/**
 * Voxly — Composer prompt at the top of the feed.
 *
 * "What's on your voice today, [name]?" — tap to jump straight into the
 * post composer. Big tap target, big avatar, three quick-action buttons
 * (Voice / Photo / Story) so users can jump straight into their preferred
 * format without bouncing through the create page.
 */
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Microphone, Camera, BookOpen, VideoCamera } from "@phosphor-icons/react";
import { avatarUrl } from "@/lib/api";

export default function FeedComposerPrompt({ user }) {
  const navigate = useNavigate();
  const name = user?.name?.split(" ")[0] || user?.username || "friend";
  return (
    <section
      className="px-4 pt-4 pb-3 border-b border-border/40"
      data-testid="feed-composer-prompt"
      aria-label="Start a new post"
    >
      <div className="flex items-center gap-3">
        <Link to="/me" aria-label="Your profile" className="shrink-0">
          <img
            src={avatarUrl(user?.picture)}
            alt={user?.name || "You"}
            className="h-11 w-11 rounded-full object-cover border-2 border-border"
            loading="lazy"
            decoding="async"
          />
        </Link>
        <button
          onClick={() => navigate("/create")}
          data-testid="feed-composer-tap"
          className="flex-1 text-left px-4 py-3 rounded-full bg-secondary/60 hover:bg-secondary/80 transition border border-border/60 text-sm text-muted-foreground"
        >
          What&apos;s on your voice today, {name}?
        </button>
      </div>
      <div className="grid grid-cols-4 gap-2 mt-3">
        <button
          onClick={() => navigate("/create")}
          data-testid="feed-composer-voice"
          className="flex items-center justify-center gap-1.5 py-2 rounded-2xl bg-primary/10 border border-primary/30 text-primary text-xs font-bold hover:bg-primary/15 transition"
        >
          <Microphone size={16} weight="fill" /> Voice
        </button>
        <button
          onClick={() => navigate("/create")}
          data-testid="feed-composer-photo"
          className="flex items-center justify-center gap-1.5 py-2 rounded-2xl bg-card border border-border text-foreground text-xs font-bold hover:bg-secondary/40 transition"
        >
          <Camera size={16} weight="fill" /> Photo
        </button>
        <button
          onClick={() => navigate("/stories/create")}
          data-testid="feed-composer-story"
          className="flex items-center justify-center gap-1.5 py-2 rounded-2xl bg-card border border-border text-foreground text-xs font-bold hover:bg-secondary/40 transition"
        >
          <BookOpen size={16} weight="fill" /> Story
        </button>
        <button
          onClick={() => navigate("/live/new")}
          data-testid="feed-composer-live"
          className="flex items-center justify-center gap-1.5 py-2 rounded-2xl bg-red-500/15 border border-red-500/40 text-red-500 text-xs font-bold hover:bg-red-500/20 transition"
        >
          <VideoCamera size={16} weight="fill" /> Live
        </button>
      </div>
    </section>
  );
}
